import type { CSSProperties, JSX, ReactNode } from 'react';
import type { StatusKind } from '@/lib/bands';
import { Icon } from './Icon';

export interface CardProps {
  readonly children: ReactNode;
  /** Navy inverted card, for the headline figure in a tile row. */
  readonly selected?: boolean | undefined;
  /** Muted treatment for a catalogued-but-unbuilt module. */
  readonly soon?: boolean | undefined;
  /** Drops the shadow, for cards nested inside another card. */
  readonly flat?: boolean | undefined;
  /** Removes padding, for a card that contains only a table. */
  readonly flush?: boolean | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
}

export function Card({
  children,
  selected = false,
  soon = false,
  flat = false,
  flush = false,
  className,
  style,
}: CardProps): JSX.Element {
  const classes = [
    'card',
    selected ? 'card--sel' : '',
    soon ? 'card--soon' : '',
    flat ? 'card--flat' : '',
    flush ? 'card--flush' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} style={style}>
      {children}
    </div>
  );
}

/** Clickable card. A real button, so it is reachable by keyboard. */
export function CardButton({
  children,
  onClick,
  selected = false,
  className,
  style,
  ariaLabel,
}: {
  readonly children: ReactNode;
  readonly onClick: () => void;
  readonly selected?: boolean | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  readonly ariaLabel?: string | undefined;
}): JSX.Element {
  const classes = ['card', selected ? 'card--sel' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      aria-label={ariaLabel}
      style={{ textAlign: 'left', cursor: 'pointer', width: '100%', font: 'inherit', ...style }}
    >
      {children}
    </button>
  );
}

export function CardHeader({
  title,
  caption,
  actions,
  children,
}: {
  readonly title?: ReactNode | undefined;
  readonly caption?: ReactNode | undefined;
  readonly actions?: ReactNode | undefined;
  readonly children?: ReactNode | undefined;
}): JSX.Element {
  return (
    <div className="card-hd">
      {children ?? (
        <div>
          {title !== undefined && <h5>{title}</h5>}
          {caption !== undefined && <span className="caption">{caption}</span>}
        </div>
      )}
      {actions}
    </div>
  );
}

/** Coloured bar bleeding to the card edges. */
export function Stripe({ background }: { readonly background: string }): JSX.Element {
  return <div className="stripe" style={{ background }} />;
}

export type NoticeTone = 'info' | 'good' | 'warn' | 'bad';

const NOTICE_CLASS: Record<NoticeTone, string> = {
  info: '',
  good: 'notice--good',
  warn: 'notice--warn',
  bad: 'notice--bad',
};

const NOTICE_ICON: Record<NoticeTone, string> = {
  info: 'info',
  good: 'checkCircle',
  warn: 'warn',
  bad: 'alert',
};

export function Notice({
  tone = 'info',
  icon,
  children,
  className,
  style,
}: {
  readonly tone?: NoticeTone | undefined;
  readonly icon?: string | undefined;
  readonly children: ReactNode;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <div className={`notice ${NOTICE_CLASS[tone]} ${className ?? ''}`} style={style}>
      <Icon name={icon ?? NOTICE_ICON[tone]} />
      <div>{children}</div>
    </div>
  );
}

/** Maps a status kind onto a notice tone. */
export const noticeToneFor = (kind: StatusKind): NoticeTone =>
  kind === 'good' ? 'good' : kind === 'warn' ? 'warn' : kind === 'bad' ? 'bad' : 'info';

export function EmptyState({
  icon = 'search',
  title,
  caption,
  action,
}: {
  readonly icon?: string | undefined;
  readonly title: string;
  readonly caption?: string | undefined;
  readonly action?: ReactNode | undefined;
}): JSX.Element {
  return (
    <div className="empty">
      <Icon name={icon} />
      <h5>{title}</h5>
      {caption !== undefined && <p className="caption">{caption}</p>}
      {action !== undefined && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

/** Dashed placeholder panel, used for scope notes and previews. */
export function Placeholder({
  eyebrow,
  children,
}: {
  readonly eyebrow: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div className="ph">
      <p className="eyebrow">{eyebrow}</p>
      <p className="body-sm" style={{ marginTop: 6 }}>
        {children}
      </p>
    </div>
  );
}

/** Gradient hero band. */
export function Hero({
  children,
  center = false,
}: {
  readonly children: ReactNode;
  readonly center?: boolean | undefined;
}): JSX.Element {
  return (
    <div className="hero" style={center ? { textAlign: 'center' } : undefined}>
      {children}
    </div>
  );
}

/** Rounded icon plate used on catalogue cards and section headers. */
export function IconPlate({
  icon,
  color,
  tint,
  size = 48,
  className,
}: {
  readonly icon: string;
  readonly color: string;
  readonly tint: string;
  readonly size?: number | undefined;
  readonly className?: string | undefined;
}): JSX.Element {
  return (
    <span
      className={`mod-icon ${className ?? ''}`}
      style={{ background: tint, color, width: size, height: size }}
    >
      <Icon name={icon} />
    </span>
  );
}

/** Horizontal rule with the standard vertical rhythm. */
export function Separator({ style }: { readonly style?: CSSProperties }): JSX.Element {
  return <div className="sep" style={style} />;
}
